import React, { useState, useContext } from "react"
import { useLazyQuery, useQuery } from "@apollo/client"
import { useNavigate, Link } from "react-router-dom"

import { UserContext } from "../../../context/userContext"

import {
  GET_STAFF_ID_BY_MEMBERSHIP_ID,
  GET_SAVED_TEMPLATE_BY_SELECTED_STAFF,
  GET_STAFF_NAME,
} from "../queries"

import useLabels from "../../../hooks/useLabels"

import { parseDate } from "../../../helpers/dateNormalization"

import { formatDate, handleOptsForFilter } from "../../../components/DataTable/helpers"
import GenericTable from "../../../components/DataTable/GenericTable"
import FilterHeader from "../../../components/DataTable/FilterHeader"
import StaffSidebar from "../../../components/sidebars/StaffSidebar"
import StaffProfileBox from "../../Staff/components/StaffProfileBox"

import { LoaderMedium } from "../../../components/Loaders"

import { Calendar } from "primereact/calendar"
import { SplitButton } from "primereact/splitbutton"
import { FilterService } from "primereact/api"
import { Button } from "primereact/button"
import { MultiSelect } from "primereact/multiselect"

import { useFilters } from "../../../components/Filters/useFilters"
import { navigateWithOrigin } from "../../../helpers/navigation"

export default function StaffIndividualSession() {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const [sessions, setSessions] = useState([])
  const [loadingPage, setLoadingPage] = useState(true)
  const [dateRange, setDateRange] = useState(null)
  const [blocks, setBlocks] = useState(null)
  const idTemplate = "individual_supervision_session"

  const [loadingLabels, labels] = useLabels(["individual_supervision_session"])

  const staff_membership_id = userCtx.current_staff_membership_id

  const currentYear = new Date().getFullYear()
  const yearRange = `2000:${currentYear + 10}`

  FilterService.register("arrayContains", (value, filter) => {
    if (!value || !Array.isArray(value)) return false
    return value.some((item) => filter.includes(item))
  })

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
    onMultiSelectFilterChange,
  } = useFilters()

  // get Staff Data by Membership ID
  const { data: staffData } = useQuery(GET_STAFF_ID_BY_MEMBERSHIP_ID, {
    variables: { staff_membership_id },
    fetchPolicy: "network-only",
    skip: !staff_membership_id,
    onError: (error) => console.error("Error fetching staff data: ", error),
  })

  const staff_id_role_id = staffData?.staffDataByStaffMembershipID?.rol_id

  const [fetchStaffNameById] = useLazyQuery(GET_STAFF_NAME, {
    fetchPolicy: "network-only",
    onError: (error) => console.error("Error fetching staff profile: ", error),
  })

  const { loading } = useQuery(GET_SAVED_TEMPLATE_BY_SELECTED_STAFF, {
    variables: {
      supervision_staff_membership_id: staff_membership_id,
      organization_id: staffData?.staffDataByStaffMembershipID?.organization_id,
      idTemplate,
    },
    skip: !staffData?.staffDataByStaffMembershipID?.organization_id,
    fetchPolicy: "network-only",
    onError: (error) => console.error("Error fetching blocks: ", error),
    onCompleted: (data) => {
      const processBlocks = async () => {
        const blocksData = data?.blocksSavedByStaffAndOrg?.data

        if (!blocksData) {
          setBlocks(null)
          return
        }

        try {
          const parsedBlocks = JSON.parse(blocksData)
          const formattedData = {}
          const staffIdsSet = new Set()

          let oldestRecordIdStamp = null
          let oldestSessionDate = null

          parsedBlocks.forEach((block) => {
            const { idStamp, block_name, values } = block
            if (!formattedData[idStamp]) formattedData[idStamp] = {}

            if (block_name === "session__supervision") {
              const sessionDate = new Date(values.session_date)

              if (!oldestSessionDate || sessionDate < oldestSessionDate) {
                oldestSessionDate = sessionDate
                oldestRecordIdStamp = idStamp
              }

              formattedData[idStamp].session_date = values.session_date
              formattedData[idStamp].program =
                values.supervision__program_identity_ids || []
              formattedData[idStamp].facilitating_staff = block.staff_id
              formattedData[idStamp].supervision__staff_membership_id =
                values.supervision__staff_membership_id
              formattedData[idStamp].topics = values.supervision_session_topic
              formattedData[idStamp].other_topics =
                values.supervision_session_topic_other_type

              if (block.staff_id) staffIdsSet.add(block.staff_id)
            } else if (
              block_name === "session__supervision__supervision_plan"
            ) {
              formattedData[idStamp].supervision_plan_review_frequency =
                values.supervision_plan_review_frequency

              const modifiedFields = values.block_modified_fields || []
              formattedData[idStamp].plan_changed =
                modifiedFields.length > 0 ? "Yes" : "No"
            } else if (block_name === "session") {
              formattedData[idStamp].location =
                values.session__location_ids || []
            }

            // normalize program_id and location_id
            if (values.supervision__program_identity_ids) {
              formattedData[idStamp].program_id = Array.isArray(
                values.supervision__program_identity_ids
              )
                ? values.supervision__program_identity_ids.map((p) => p.key)
                : []
            }
            if (values.session__location_ids) {
              formattedData[idStamp].location_id = Array.isArray(
                values.session__location_ids
              )
                ? values.session__location_ids.map((l) => l.key)
                : []
            }
          })

          // mark oldest record as changed
          if (oldestRecordIdStamp && formattedData[oldestRecordIdStamp]) {
            formattedData[oldestRecordIdStamp].plan_changed = "Yes"
          }

          const staffIds = Array.from(staffIdsSet)
          let staffNameDict = {}

          for (const staffId of staffIds) {
            try {
              const { data } = await fetchStaffNameById({
                variables: {
                  staff_id: staffId,
                  organization_id:
                    staffData?.staffDataByStaffMembershipID?.organization_id,
                },
              })
              staffNameDict[staffId] =
                data?.getStaffName?.data || "Unknown Staff"
            } catch (err) {
              console.error("fetchStaffNameById error for", staffId, err)
              staffNameDict[staffId] = "Unknown Staff"
            }
          }

          const tableData = Object.keys(formattedData).map((idStamp) => {
            const row = formattedData[idStamp]
            const staffName =
              staffNameDict[row.facilitating_staff] || "Unknown Staff"

            const isSubject =
              row.supervision__staff_membership_id ===
              userCtx.session.user.staff_membership_id
            const canEdit = !isSubject

            return {
              date: parseDate(row.session_date),
              locations: Array.isArray(row.location)
                ? row.location.map((loc) => loc.label).join(", ")
                : "",
              facilitating_staff: staffName,
              topics: row.topics,
              plan_changed: row.plan_changed,
              programs: Array.isArray(row.program)
                ? row.program.map((p) => p.label).join(", ")
                : "",
              actions:
                canEdit && userCtx.session.user.staff_role < 5 ? (
                  <SplitButton
                    label="View"
                    onClick={() => {
                      userCtx.changeParticipant(undefined)
                      navigateWithOrigin(
                        navigate,
                        `/form/view/individual_supervision_session/${idStamp}`
                      )
                    }}
                    model={[
                      {
                        label: "Edit",
                        command: () =>
                          navigate(
                            `/form/individual_supervision_session/1/${idStamp}`
                          ),
                      },
                    ]}
                  />
                ) : (
                  <Button
                    label="View"
                    onClick={() => {
                      userCtx.changeParticipant(undefined)
                      navigateWithOrigin(
                        navigate,
                        `/form/view/individual_supervision_session/${idStamp}`
                      )
                    }}
                  />
                ),
            }
          })

          setBlocks(tableData)
          setSessions(tableData)
          setLoadingPage(false)
        } catch (error) {
          console.error("Error parsing blocks data:", error)
          setBlocks(null)
        }
      }

      processBlocks()
    },
  })

  if (loadingPage || loading) return <LoaderMedium />

  const uniqueLocations = handleOptsForFilter(blocks, "locations")
  const uniqueFacilitatingStaff = handleOptsForFilter(
    blocks,
    "facilitating_staff"
  )
  const uniquePrograms = handleOptsForFilter(blocks, "programs")

  const filterConfig = [
    {
      label: "Date",
      value: dateRange,
      options: null,
      onChange: (e) => {
        setDateRange(e.value)
        onFilterChange("date", e.value)
      },
      onClear: () => {
        setDateRange(null)
        onFilterChange("date", null)
      },
      enableFilter: true,
      component: (
        <Calendar
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.value)
            onFilterChange("date", e.value)
          }}
          placeholder="Select Date Range"
          selectionMode="range"
          dateFormat="mm/dd/yy"
          readOnlyInput
          numberOfMonths={2}
          showIcon
          showButtonBar
          yearNavigator
          yearRange={yearRange}
        />
      ),
    },
    {
      label: "Locations",
      value: filters.locations?.value || "",
      options: uniqueLocations,
      onChange: (e) => onFilterChange("locations", e.value),
      onClear: () => onFilterChange("locations", null),
      placeholder: "Select Locations",
    },
    {
      label: "Programs",
      value: filters.programs?.value || "",
      options: uniquePrograms,
      onChange: (e) => onFilterChange("programs", e.value),
      onClear: () => onFilterChange("programs", null),
      placeholder: "Select Programs",
    },
    {
      label: "Facilitating Staff",
      value: filters.facilitating_staff?.value || "",
      options: uniqueFacilitatingStaff,
      onChange: (e) => onFilterChange("facilitating_staff", e.value),
      onClear: () => onFilterChange("facilitating_staff", null),
      placeholder: "Select Facilitating Staff",
    },
    {
      label: "Topics",
      component: (
        <MultiSelect
          value={filters.topics?.value || []}
          options={[
            ...new Set(sessions.flatMap((session) => session.topics || [])),
          ]}
          onChange={(e) => onMultiSelectFilterChange("topics", e.value)}
          placeholder="Select Topics"
          display="chip"
          style={{
            maxWidth: "200px",
            overflowY: "auto",
          }}
        />
      ),
      placeholder: "Select Topics",
    },
  ]

  const handleClearAllFilters = () => {
    clearAllFilters()
    setDateRange(null)
  }

  const columns = [
    {
      field: "date",
      header: "Date",
      sortable: true,
      filter: true,
      body: (rowData) => formatDate(rowData.date),
    },
    {
      field: "locations",
      header: "Location",
      sortable: true,
      filter: true,
    },
    {
      field: "facilitating_staff",
      header: "Facilitating Staff",
      sortable: true,
      filter: true,
    },
    {
      field: "topics",
      header: "Topics",
      sortable: true,
      filter: true,
      body: (rowData) => {
        return rowData.topics?.join(", ") || "None"
      },
    },
    {
      field: "plan_changed",
      header: "Plan Changed?",
      sortable: true,
      filter: true,
    },
    {
      field: "programs",
      header: "Program ID",
      sortable: true,
      filter: true,
    },
    {
      field: "actions",
      header: "Actions",
      body: (rowData) => rowData.actions,
    },
  ]

  if (loadingLabels) return null

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <StaffSidebar />
      </div>
      <div className="main-content">
        <StaffProfileBox />
        <h1>{labels?.individual_supervision_session}</h1>
        {userCtx.session.user.staff_role >= 0 &&
        userCtx.session.user.staff_role <= 4 &&
        (userCtx.session.user.staff_role !== 4 ||
          userCtx.current_staff_membership_id !==
            userCtx.session.user.staff_membership_id) &&
        staff_id_role_id !== "-1" ? (
          <Link to="/form/individual_supervision_session/1" className="btn">
            Start {labels?.individual_supervision_session}
          </Link>
        ) : null}
        <br />
        <br />
        <GenericTable
          data={blocks}
          columns={columns}
          filters={filters}
          header={
            <FilterHeader
              filters={filterConfig}
              globalFilterValue={globalFilterValue}
              onGlobalFilterChange={onGlobalFilterChange}
              clearGlobalFilter={clearGlobalFilter}
              clearAllFilters={handleClearAllFilters}
            />
          }
          paginator
          rows={50}
        />
      </div>
    </div>
  )
}
